import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure
} from '@nextui-org/react';
import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/firebase';
import { apiTry } from '../lib/api';
import { deleteAgencyCascade, purgeSummary } from '../lib/deleteAgency';
import { applySort } from '../lib/sort';
import { useAuth } from '../hooks/useAuth';
import { crmText } from '../lib/crmI18n';
import { crmDenied } from '../lib/crmAccess';

const PLAN_RANK = { rookie: 0, silver: 1, gold: 2, titanium: 3 };

const PAGE_LIMIT = 2000;

// Mismos planes que renderer/tabs/agencias/shared.js (PLANS) en la app
// Electron. Los créditos son informativos: el límite real sale del plan +
// agencies/{id}.planOverrides (se edita desde el Admin dentro de la app).
const PLAN_OPTIONS = [
  { key: 'rookie', label: 'Rookie', maxCredits: 40 },
  { key: 'silver', label: 'Silver', maxCredits: 100 },
  { key: 'gold', label: 'Gold', maxCredits: 200 },
  { key: 'titanium', label: 'Titanium', maxCredits: 400 }
];

const PLAN_COLOR = { rookie: 'default', silver: 'default', gold: 'warning', titanium: 'primary' };

// Agencia de prueba (las de demostración que crea el equipo): la misma marca
// que usa el Worker (cf-worker/src/lib/agencyDirectory.js › isDemoAgency).
const isTestAgency = (a) => a?.testData === true;

const DIRECTORY_ENDPOINT = '/api/backoffice/agency-directory';

// Un 404 es que el Worker que contesta aún no tiene el interruptor (sin desplegar).
function directoryError(res, doing) {
  if (res.status === 404) return `${doing}: el Worker aún no tiene este interruptor. Hay que desplegarlo.`;
  if (res.status === 403) return `${doing}: solo un admin puede cambiarlo.`;
  const detail = typeof res.data === 'string' ? res.data : res.data?.error;
  return `${doing} (${res.status}${detail ? `: ${detail}` : ''}).`;
}

// Límites por plan (espejo de shared.PLANS; 9999 = ilimitado) para bloquear
// un downgrade cuando la agencia excede el plan destino.
const PLAN_LIMITS = {
  rookie: { maxCredits: 40, maxManagers: 1, maxProfiles: 2, missions: false, agencyEvents: false, autoTickets: false },
  silver: { maxCredits: 100, maxManagers: 4, maxProfiles: 10, missions: true, agencyEvents: true, autoTickets: false },
  gold: { maxCredits: 200, maxManagers: 10, maxProfiles: 25, missions: true, agencyEvents: true, autoTickets: true },
  titanium: { maxCredits: 400, maxManagers: 9999, maxProfiles: 9999, missions: true, agencyEvents: true, autoTickets: true }
};
const CREDIT_COST = { premium: 2, performance: 1 };

async function planDowngradeIssues(agency, targetPlan) {
  const lim = { ...PLAN_LIMITS[targetPlan] };
  const ov = agency?.planOverrides?.limits || {};
  for (const k of ['maxCredits', 'maxManagers', 'maxProfiles']) {
    if (ov[k] != null && ov[k] !== '' && Number.isFinite(Number(ov[k]))) lim[k] = Number(ov[k]);
  }
  const issues = [];
  const owner = agency.ownerId || agency.ownerUid || null;
  const staff = Object.entries(agency.licStaff || {}).filter(([uid, s]) => s && s.active !== false && uid !== owner).length;
  const members = await getDocs(query(collection(db, 'agency_members'), where('agencyId', '==', agency.id)));
  let credits = 0;
  members.forEach((d) => { const m = d.data(); if (m.licenseType && m.licenseType !== 'none') credits += CREDIT_COST[m.licenseType] || 2; });
  const profiles = await getDocs(query(collection(db, 'agency_profiles'), where('agencyId', '==', agency.id)));
  const events = await getDocs(query(collection(db, 'agency_events'), where('agencyId', '==', agency.id), where('active', '==', true)));
  const missions = await getDocs(query(collection(db, 'agency_challenges'), where('agencyId', '==', agency.id)));
  const now = Date.now();
  const toMs = (v) => (v && v.toMillis) ? v.toMillis() : (v ? new Date(v).getTime() : 0);
  let activeMissions = 0;
  missions.forEach((d) => { const c = d.data(); if (c.archived || c.draft || c.active === false) return; const end = toMs(c.endDate); const start = toMs(c.startDate); if ((end && end < now) || (start && start > now)) return; activeMissions++; });
  if (lim.maxCredits < 9999 && credits > lim.maxCredits) issues.push(`Créditos asignados: ${credits} / ${lim.maxCredits}`);
  if (lim.maxManagers < 9999 && staff > lim.maxManagers) issues.push(`Managers: ${staff} / ${lim.maxManagers}`);
  if (lim.maxProfiles < 9999 && profiles.size > lim.maxProfiles) issues.push(`Perfiles LIVE: ${profiles.size} / ${lim.maxProfiles}`);
  if (!lim.missions && activeMissions > 0) issues.push(`Misiones activas: ${activeMissions} (no incluidas en ${targetPlan})`);
  if (!lim.agencyEvents && events.size > 0) issues.push(`Eventos de agencia activos: ${events.size} (no incluidos en ${targetPlan})`);
  if (!lim.autoTickets && (agency.ticketsMode === 'auto' || agency.ticketsMode === 'hybrid')) issues.push(`Tickets en modo ${agency.ticketsMode === 'hybrid' ? 'híbrido' : 'automático'} (no incluido en ${targetPlan})`);
  return issues;
}

// Créditos efectivos: override del Admin si existe, si no el del plan.
export function agencyCredits(a) {
  const ov = a?.planOverrides?.limits?.maxCredits;
  if (ov != null && ov !== '' && Number.isFinite(Number(ov))) return Number(ov);
  const p = PLAN_OPTIONS.find((x) => x.key === (a?.plan || 'silver'));
  return p ? p.maxCredits : 0;
}

// Puerto directo de generateAgencyInviteCode() en admin.js:19.
// Genera un ID determinista a partir del nombre — mismo algoritmo.
function generateAgencyId(name) {
  if (!name || !name.trim()) return '';
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const normalized = name.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  let h = 5381;
  for (let i = 0; i < normalized.length; i++) {
    h = Math.abs((h * 33 + normalized.charCodeAt(i)) % 2147483647);
  }
  let suffix = '';
  for (let i = 0; i < 6; i++) {
    suffix += chars[h % chars.length];
    h = Math.abs((h * 17 + 13) % 2147483647);
  }
  return normalized.slice(0, 10) + '-' + suffix;
}

const EMPTY_FORM = {
  id: '',
  name: '',
  plan: 'silver',
  region: '',
  logo: '🏢',
  validated: false
};

export default function Agencias() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [validFilter, setValidFilter] = useState('all');
  // Reales o de prueba (testData: true).
  const [kindFilter, setKindFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [sortDescriptor, setSortDescriptor] = useState({
    column: 'name',
    direction: 'ascending'
  });
  const navigate = useNavigate();
  const { canWrite, user } = useAuth();

  const detail = useDisclosure();
  const form = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formId, setFormId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Solicitudes nuevas de «¿Tu agencia no aparece? Aplica aquí» (contador del botón).
  const [newApplications, setNewApplications] = useState(null);
  useEffect(() => {
    getCountFromServer(query(collection(db, 'agency_applications'), where('status', '==', 'new')))
      .then((snap) => setNewApplications(snap.data().count))
      .catch(() => setNewApplications(null));
  }, []);

  // Avisos nuevos «ha gastado el 80 % de sus créditos» (los crea el Worker).
  const [newCreditAlerts, setNewCreditAlerts] = useState(null);
  useEffect(() => {
    getCountFromServer(query(collection(db, 'agency_credit_alerts'), where('status', '==', 'new')))
      .then((snap) => setNewCreditAlerts(snap.data().count))
      .catch(() => setNewCreditAlerts(null));
  }, []);

  // «Ocultar las agencias de prueba del Explorador» (testData: true). Lo guarda
  // el Worker (cf-worker/src/api/agencyDirectory.js, D1 app_config['agency_directory']).
  const [dirConfig, setDirConfig] = useState(null); // { hideTest } | null mientras carga
  const [dirSaving, setDirSaving] = useState(false);
  const [dirError, setDirError] = useState(null);
  useEffect(() => {
    let alive = true;
    apiTry(DIRECTORY_ENDPOINT).then((res) => {
      if (!alive) return;
      if (res.ok) setDirConfig({ hideTest: res.data?.config?.hideTest === true });
      else setDirError(directoryError(res, 'No se pudo leer si se ocultan'));
    }).catch((e) => { if (alive) setDirError(`No se pudo leer si se ocultan (${e.message || 'error'}).`); });
    return () => { alive = false; };
  }, []);

  async function setHideTest(hideTest) {
    const before = dirConfig;
    setDirConfig({ hideTest });
    setDirSaving(true);
    setDirError(null);
    try {
      const res = await apiTry(DIRECTORY_ENDPOINT, { method: 'PUT', body: { config: { hideTest } } });
      if (!res.ok) throw new Error(directoryError(res, 'No se ha guardado'));
      setDirConfig({ hideTest: res.data?.config?.hideTest === true });
      setToast({
        type: 'ok',
        msg: hideTest
          ? 'Agencias de prueba ocultas: en menos de un minuto el Explorador solo enseña las reales.'
          : 'Agencias de prueba visibles otra vez: en menos de un minuto vuelven al Explorador.'
      });
    } catch (e) {
      setDirConfig(before);
      setDirError(e.message || 'No se ha guardado.');
    } finally {
      setDirSaving(false);
    }
  }

  // Suscripción en tiempo real a agencies.
  useEffect(() => {
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'agencies'),
      orderBy('name'),
      fsLimit(PAGE_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAgencies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[Agencias] onSnapshot error:', err);
        setError(err.message || 'Error cargando agencias');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const counts = useMemo(() => {
    const acc = {
      all: agencies.length,
      silver: 0,
      gold: 0,
      titanium: 0,
      other: 0,
      validated: 0,
      pending: 0,
      test: 0,
      testListed: 0
    };
    for (const a of agencies) {
      const p = a.plan || 'silver';
      if (acc[p] !== undefined) acc[p]++;
      else acc.other++;
      if (a.validated) acc.validated++;
      else acc.pending++;
      if (isTestAgency(a)) {
        acc.test++;
        // Las que el Explorador enseñaría (validadas y publicadas; el plan lo mira el Worker).
        if (a.validated === true && a.published !== false) acc.testListed++;
      }
    }
    return acc;
  }, [agencies]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = agencies.filter((a) => {
      if (planFilter !== 'all' && (a.plan || 'silver') !== planFilter) return false;
      if (validFilter === 'validated' && !a.validated) return false;
      if (validFilter === 'pending' && a.validated) return false;
      if (kindFilter === 'real' && isTestAgency(a)) return false;
      if (kindFilter === 'test' && !isTestAgency(a)) return false;
      if (!needle) return true;
      const name = (a.name || '').toLowerCase();
      const id = (a.id || '').toLowerCase();
      const region = (a.region || '').toLowerCase();
      return name.includes(needle) || id.includes(needle) || region.includes(needle);
    });

    return applySort(base, sortDescriptor, {
      name: (a) => (a.name || '').toLowerCase(),
      plan: (a) => PLAN_RANK[a.plan || 'silver'] ?? -1,
      region: (a) => (a.region || '').toLowerCase(),
      credits: (a) => agencyCredits(a),
      validated: (a) => (a.validated === true ? 0 : 1)
    });
  }, [agencies, search, planFilter, validFilter, kindFilter, sortDescriptor]);

  function openNew() {
    setFormData(EMPTY_FORM);
    setFormId(null);
    form.onOpen();
  }

  function openEdit(a) {
    setFormData({
      id: a.id,
      name: a.name || '',
      plan: a.plan || 'silver',
      region: a.region || '',
      logo: a.logo || '🏢',
      validated: a.validated === true
    });
    setFormId(a.id);
    form.onOpen();
  }

  function openDetail(a) {
    setSelected(a);
    detail.onOpen();
  }

  async function saveForm() {
    if (!formData.name.trim()) {
      setToast({ type: 'error', msg: 'El nombre es obligatorio.' });
      return;
    }
    setSaving(true);
    try {
      const now = serverTimestamp();
      const basePayload = {
        name: formData.name.trim(),
        plan: formData.plan,
        region: formData.region.trim() || null,
        logo: formData.logo.trim() || '🏢',
        validated: formData.validated,
        updatedAt: now
      };

      if (formId) {
        // Edit existing — bloquear el cambio de plan si la agencia excede
        // el plan destino (debe ajustarlo antes desde su consola).
        const current = agencies.find((a) => a.id === formId);
        if (current && current.plan !== formData.plan) {
          const issues = await planDowngradeIssues(current, formData.plan);
          if (issues.length) {
            throw new Error(`La agencia excede el plan ${formData.plan} y debe ajustarlo antes: ${issues.join(' · ')}`);
          }
        }
        if (formData.validated) {
          basePayload.validatedAt = now;
          basePayload.validatedBy = auth.currentUser?.uid || null;
        }
        await updateDoc(doc(db, 'agencies', formId), basePayload);
        const welcomed = formData.validated ? await ownerWelcome(formId) : '';
        setToast({ type: 'ok', msg: `Agencia actualizada.${welcomed}` });
      } else {
        // Create new — ID determinista a partir del nombre
        const newId = generateAgencyId(formData.name);
        if (!newId) {
          throw new Error('No se pudo generar un ID para el nombre dado.');
        }
        if (agencies.some((a) => a.id === newId)) {
          throw new Error(`Ya existe una agencia con ID ${newId}. Cambia el nombre.`);
        }
        await setDoc(doc(db, 'agencies', newId), {
          ...basePayload,
          createdAt: now,
          createdBy: auth.currentUser?.uid || null,
          // Nace sin publicar: la publica su Owner desde la consola cuando la
          // tenga lista (o un ADMIN desde aquí). Hasta entonces no la ven sus creadores.
          published: false,
          validatedAt: formData.validated ? now : null,
          validatedBy: formData.validated ? auth.currentUser?.uid : null
        });
        setToast({ type: 'ok', msg: `Agencia creada con ID ${newId}.` });
      }
      form.onClose();
      // onSnapshot refresca solo.
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  // Bienvenida al Owner («Tu agencia ya está activa en TikControl»): al
  // validar, el Worker se la manda a su Owner si lo tiene y aún no la recibió
  // (una vez por Owner). Devuelve el texto que se añade al aviso, o ''.
  async function ownerWelcome(agencyId) {
    try {
      const res = await apiTry('/api/agency/owner-welcome', { method: 'POST', body: { agencyId } });
      return res.ok && res.data?.sent > 0 ? ' Correo de bienvenida enviado al Owner.' : '';
    } catch (_) {
      return '';
    }
  }

  async function toggleValidated(a) {
    const next = !a.validated;
    try {
      await updateDoc(doc(db, 'agencies', a.id), {
        validated: next,
        validatedAt: next ? serverTimestamp() : null,
        validatedBy: next ? auth.currentUser?.uid || null : null,
        updatedAt: serverTimestamp()
      });
      setAgencies((list) =>
        list.map((x) => (x.id === a.id ? { ...x, validated: next } : x))
      );
      const welcomed = next ? await ownerWelcome(a.id) : '';
      setToast({
        type: 'ok',
        msg: next ? `Agencia validada.${welcomed}` : 'Agencia invalidada.'
      });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  // Borrado en cascada: el ID de agencia se escribe a mano y se reutiliza, así
  // que dejar los datos colgando hacía que una agencia nueva con el mismo ID
  // heredara creadores, misiones, eventos y roles de la anterior.
  async function handleDelete(a) {
    const warn = [
      `¿Borrar "${a.name || a.id}"?`,
      '',
      'Se borrarán también sus creadores (con el histórico importado del backstage), '
        + 'misiones, eventos, perfiles, licencias, biblioteca, whitelist y los roles de '
        + 'acceso de sus owners y managers.',
      '',
      'A sus creadores con licencia se les retira (se reanuda el cobro que pausó la '
        + 'agencia y vuelven a su plan), y se borran su Backstage, archivos, tracking y avisos.',
      '',
      'Esta acción no se puede deshacer.'
    ].join('\n');
    if (!confirm(warn)) return;
    const typed = prompt(`Escribe ${a.id} para confirmar el borrado:`);
    if ((typed || '').trim().toUpperCase() !== String(a.id).toUpperCase()) {
      setToast({ type: 'error', msg: 'ID incorrecto: no se ha borrado nada.' });
      return;
    }
    try {
      setToast({ type: 'ok', msg: 'Borrando agencia y sus datos…' });
      const res = await deleteAgencyCascade(db, a.id);
      const docs = Object.values(res.deleted || {}).reduce((acc, n) => acc + (n || 0), 0);
      const extra = res.purge?.report ? ` · Limpieza: ${purgeSummary(res.purge.report)}` : '';
      setAgencies((list) => list.filter((x) => x.id !== a.id));
      if (res.errors?.length) {
        console.warn('[Agencias] borrado parcial:', res.errors);
        setToast({ type: 'error', msg: `Agencia eliminada, pero algo falló: ${res.errors[0]} (en «Restos de borradas» se puede repetir la limpieza)${extra}` });
      } else {
        setToast({ type: 'ok', msg: `Agencia eliminada · ${docs} documentos borrados${extra}.` });
      }
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Agencias</h1>
          <div className="flex items-center gap-2 text-sm text-default-500">
            {loading ? (
              'Cargando…'
            ) : (
              <>
                <span>
                  {filtered.length} de {agencies.length} (agencies)
                </span>
                {agencies.length >= PAGE_LIMIT && (
                  <Chip size="sm" color="warning" variant="flat">
                    límite {PAGE_LIMIT} alcanzado
                  </Chip>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!crmDenied(user?.uid) && (
            <Button variant="flat" onPress={() => navigate('/agencias/crm')}>
              {crmText.title}
            </Button>
          )}
          <Button variant="flat" onPress={() => navigate('/agencias/lanzamiento')}>
            Lanzamiento
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/live-fest')}>
            LIVE FEST
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/avisos-creditos')}>
            Avisos de créditos
            {newCreditAlerts > 0 && <Chip size="sm" color="warning">{newCreditAlerts}</Chip>}
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/preventa')}>
            Preventa
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/restos')}>
            Restos de borradas
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/solicitudes')}>
            Solicitudes
            {newApplications > 0 && <Chip size="sm" color="primary">{newApplications}</Chip>}
          </Button>
          {canWrite && (
            <Button color="primary" onPress={openNew}>
              + Nueva agencia
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-default-200 bg-content1 px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Ocultar las agencias de prueba del Explorador</p>
          <p className="text-xs text-default-500">
            {loading
              ? 'Contando las agencias de prueba…'
              : `${counts.test} agencias de prueba (marcadas como datos de prueba)` +
                (counts.testListed !== counts.test ? `, ${counts.testListed} validadas y publicadas` : '') + '. '}
            {dirConfig == null
              ? ''
              : dirConfig.hideTest
                ? 'Ahora no salen: el Explorador de la app solo enseña las agencias reales y la ficha de una de prueba no se abre.'
                : 'Ahora salen en el Explorador de la app junto a las reales.'}
            {' '}El cambio tarda hasta un minuto en verse.
          </p>
          {dirError && <p className="text-xs text-danger-600">{dirError}</p>}
        </div>
        <Switch
          isSelected={dirConfig?.hideTest === true}
          isDisabled={!canWrite || dirConfig == null || dirSaving}
          onValueChange={setHideTest}
          aria-label="Ocultar las agencias de prueba del Explorador"
        >
          {dirSaving ? 'Guardando…' : dirConfig?.hideTest ? 'Ocultas' : 'Visibles'}
        </Switch>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por nombre, ID o región…"
          value={search}
          onValueChange={setSearch}
          isClearable
          size="sm"
          className="max-w-sm"
        />
        <Tabs
          size="sm"
          selectedKey={planFilter}
          onSelectionChange={(k) => setPlanFilter(String(k))}
          aria-label="Filtrar por plan"
        >
          <Tab key="all" title={`Todos (${counts.all})`} />
          <Tab key="silver" title={`Silver (${counts.silver})`} />
          <Tab key="gold" title={`Gold (${counts.gold})`} />
          <Tab key="titanium" title={`Titanium (${counts.titanium})`} />
          {counts.other > 0 && <Tab key="other" title={`Otros (${counts.other})`} />}
        </Tabs>
        <Tabs
          size="sm"
          selectedKey={validFilter}
          onSelectionChange={(k) => setValidFilter(String(k))}
          aria-label="Filtrar por validación"
        >
          <Tab key="all" title="Todos" />
          <Tab key="validated" title={`Validadas (${counts.validated})`} />
          <Tab key="pending" title={`Pendientes (${counts.pending})`} />
        </Tabs>
        <Tabs
          size="sm"
          selectedKey={kindFilter}
          onSelectionChange={(k) => setKindFilter(String(k))}
          aria-label="Filtrar reales o de prueba"
        >
          <Tab key="all" title="Todas" />
          <Tab key="real" title={`Reales (${counts.all - counts.test})`} />
          <Tab key="test" title={`TEST (${counts.test})`} />
        </Tabs>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      {toast && (
        <div
          className={`rounded-md border p-3 text-sm ${
            toast.type === 'ok'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-danger-200 bg-danger-50 text-danger-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>
              cerrar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <Table
          aria-label="Tabla de agencias"
          isStriped
          removeWrapper
          classNames={{ th: 'bg-default-100' }}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        >
          <TableHeader>
            <TableColumn key="name" allowsSorting>Agencia</TableColumn>
            <TableColumn key="plan" allowsSorting>Plan</TableColumn>
            <TableColumn key="region" allowsSorting>Región</TableColumn>
            <TableColumn key="credits" allowsSorting>Créditos</TableColumn>
            <TableColumn key="validated" allowsSorting>Estado</TableColumn>
            <TableColumn align="end">Acciones</TableColumn>
          </TableHeader>
          <TableBody emptyContent="No hay agencias.">
            {filtered.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{a.logo || '🏢'}</span>
                    <div className="flex flex-col">
                      <span className="flex items-center gap-1 font-medium">
                        {a.name || '—'}
                        {isTestAgency(a) && (
                          <Chip size="sm" variant="flat" color="secondary">
                            TEST
                          </Chip>
                        )}
                      </span>
                      <span className="text-xs text-default-400">{a.id}</span>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={PLAN_COLOR[a.plan] || 'default'}
                  >
                    {a.plan || 'silver'}
                  </Chip>
                </TableCell>
                <TableCell>{a.region || '—'}</TableCell>
                <TableCell>{agencyCredits(a)}{a.planOverrides ? ' *' : ''}</TableCell>
                <TableCell>
                  {a.validated ? (
                    <Chip size="sm" color="success" variant="flat">
                      Validada
                    </Chip>
                  ) : (
                    <Chip size="sm" color="warning" variant="flat">
                      Pendiente
                    </Chip>
                  )}
                  {/* Sin publicar por su Owner: sus creadores aún no la ven. La publica
                      él desde la consola (Agencia), una sola vez: aquí solo se ve. */}
                  {a.published === false && (
                    <Chip size="sm" color="default" variant="flat" className="ml-1">
                      Sin publicar
                    </Chip>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={() => navigate(`/agencias/${encodeURIComponent(a.id)}`)}
                    >
                      Perfil
                    </Button>
                    {canWrite && (
                      <>
                        <Button size="sm" variant="light" onPress={() => openDetail(a)}>
                          JSON
                        </Button>
                        <Button size="sm" variant="flat" onPress={() => openEdit(a)}>
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="flat"
                          color={a.validated ? 'warning' : 'success'}
                          onPress={() => toggleValidated(a)}
                        >
                          {a.validated ? 'Invalidar' : 'Validar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="flat"
                          color="danger"
                          onPress={() => handleDelete(a)}
                        >
                          Eliminar
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Detalle */}
      <Modal isOpen={detail.isOpen} onClose={detail.onClose} size="3xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>
            <div className="flex flex-col">
              <span>{selected?.name || selected?.id}</span>
              <span className="text-xs font-normal text-default-400">{selected?.id}</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
              {selected ? JSON.stringify(selected, null, 2) : ''}
            </pre>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={detail.onClose}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Crear / editar */}
      <Modal
        isOpen={form.isOpen}
        onClose={form.onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>{formId ? 'Editar agencia' : 'Nueva agencia'}</ModalHeader>
          <ModalBody className="flex flex-col gap-3">
            <Input
              label="Nombre"
              value={formData.name}
              onValueChange={(v) => setFormData((f) => ({ ...f, name: v }))}
              isRequired
              description={
                !formId
                  ? `ID previsto: ${generateAgencyId(formData.name) || '—'}`
                  : undefined
              }
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Plan"
                selectedKeys={[formData.plan]}
                onSelectionChange={(keys) => {
                  const plan = String(Array.from(keys)[0] || 'silver');
                  setFormData((f) => ({ ...f, plan }));
                }}
                description={`Créditos del plan: ${(PLAN_OPTIONS.find((p) => p.key === formData.plan) || {}).maxCredits ?? '—'} (condiciones personalizadas: Admin en la app)`}
              >
                {PLAN_OPTIONS.map((p) => (
                  <SelectItem key={p.key}>{p.label}</SelectItem>
                ))}
              </Select>
              <Input
                label="Región"
                value={formData.region}
                onValueChange={(v) => setFormData((f) => ({ ...f, region: v }))}
                placeholder="EU, LATAM, US…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Logo"
                value={formData.logo}
                onValueChange={(v) => setFormData((f) => ({ ...f, logo: v }))}
                description="Emoji o URL (la subida de imagen va en Electron)"
              />
            </div>
            <Switch
              isSelected={formData.validated}
              onValueChange={(v) => setFormData((f) => ({ ...f, validated: v }))}
            >
              Validada
            </Switch>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={form.onClose} isDisabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" onPress={saveForm} isLoading={saving}>
              {formId ? 'Guardar' : 'Crear'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
